import { useState } from 'react';
import { AlertTriangle, BarChart3 } from 'lucide-react';
import type { QuotaRunResponse, QuotaScheduleRow } from '../../api/client';
import { fmt } from '@cca/ui';
import { HBarChart, PieChart, type ChartRow, type PieSlice } from './QuotaCharts';

// Shared results schedule for both the stored run and the what-if scenario —
// they return the identical row shape. Per church: the engine quota beside the
// old-method current quota, with the delta, and an expandable "why" that walks
// the engine pipeline (smoothing → bands → cap → phase-in → yoy → floor).

const FLAG_LABEL: Record<string, string> = {
  no_data: 'No return data',
  insufficient_years: 'Too few years',
  null_income: 'Missing a year',
  exclusions_exceed_income: 'Exclusions > income',
  carried_forward: 'Carried-forward year',
};

function deltaStyle(delta: number): React.CSSProperties {
  if (delta > 0) return { color: 'var(--red)' };
  if (delta < 0) return { color: 'var(--green)' };
  return { color: 'var(--text2)' };
}

function WhyBreakdown({ row }: { row: QuotaScheduleRow }) {
  const phased = row.full_quota * row.phase_in_factor;
  const steps: { label: string; value: string; note?: string | undefined }[] = [
    {
      label: 'Smoothed assessable',
      value: fmt(row.smoothed_assessable),
      note: row.years_used.length ? `mean of ${row.years_used.join(', ')}` : undefined,
    },
    {
      label: 'Full quota (bands)',
      value: fmt(row.full_quota),
      note: row.cap_applied ? 'cap applied' : undefined,
    },
    {
      label: `Phase-in ×${row.phase_in_factor}`,
      value: fmt(phased),
    },
    ...(row.yoy_cap_applied
      ? [{ label: 'Year-on-year cap', value: 'applied', note: 'limited vs prior year' }]
      : []),
    ...(row.floor_applied ? [{ label: 'Floor', value: 'applied', note: 'minimum quota' }] : []),
    ...(row.collar_applied
      ? [{ label: 'Collar', value: 'applied', note: 'held at last year’s quota' }]
      : []),
    { label: 'Payable quota', value: fmt(row.payable_quota) },
  ];
  return (
    <div style={{ padding: '8px 12px 12px 30px', background: 'var(--bg2, #faf8f4)' }}>
      <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
        <tbody>
          {steps.map((s, i) => (
            <tr key={i}>
              <td style={{ padding: '2px 16px 2px 0', color: 'var(--text2)' }}>{s.label}</td>
              <td style={{ padding: '2px 12px 2px 0', textAlign: 'right', fontWeight: 600 }}>
                {s.value}
              </td>
              <td style={{ padding: '2px 0', color: 'var(--text2)', fontStyle: 'italic' }}>
                {s.note ?? ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ScheduleTable({ run }: { run: QuotaRunResponse }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showCharts, setShowCharts] = useState(true);
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const totalCurrent = run.results.reduce((s, r) => s + (r.current_quota ?? 0), 0);
  const totalDelta = run.total_payable - totalCurrent;
  const notComputable = run.results.filter((r) => !r.computable).length;

  // Chart data — computable churches only (the rest have no quota), highest first.
  const computable = [...run.results]
    .filter((r) => r.computable)
    .sort((a, b) => b.payable_quota - a.payable_quota);
  const compareRows: ChartRow[] = computable.map((r) => ({
    name: r.name,
    values: [r.payable_quota, r.current_quota],
  }));
  const shareSlices: PieSlice[] = computable.map((r) => ({ name: r.name, value: r.payable_quota }));

  return (
    <div>
      <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
        <Stat label="Total engine quota" value={`$${fmt(run.total_payable)}`} />
        <Stat label="Total current quota" value={`$${fmt(totalCurrent)}`} />
        <Stat
          label="Difference"
          value={`${totalDelta >= 0 ? '+' : '−'}$${fmt(Math.abs(totalDelta))}`}
          style={deltaStyle(totalDelta)}
        />
        <Stat label="Churches" value={String(run.count)} />
        {notComputable > 0 && (
          <Stat
            label="Need manual assessment"
            value={String(notComputable)}
            style={{ color: 'var(--red)' }}
          />
        )}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr
            style={{
              textAlign: 'right',
              color: 'var(--text2)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <th style={{ textAlign: 'left', padding: 6 }}>Church</th>
            <th style={{ padding: 6 }}>Smoothed</th>
            <th style={{ padding: 6 }}>Engine quota</th>
            <th style={{ padding: 6 }}>Current quota</th>
            <th style={{ padding: 6 }}>Δ</th>
            <th style={{ textAlign: 'left', padding: 6 }}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {run.results.map((r) => {
            const open = expanded.has(r.church_id);
            const delta =
              r.computable && r.current_quota != null ? r.payable_quota - r.current_quota : null;
            return (
              <Row
                key={r.church_id}
                r={r}
                open={open}
                delta={delta}
                onToggle={() => toggle(r.church_id)}
              />
            );
          })}
          {run.results.length === 0 && (
            <tr>
              <td colSpan={6} style={{ padding: 12, color: 'var(--text2)', fontSize: 13 }}>
                No churches. Sync churches on the Assessment Base tab first.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {computable.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setShowCharts((v) => !v)}
            title={showCharts ? 'Hide charts' : 'Show charts'}
          >
            <BarChart3 size={14} />
            &nbsp;{showCharts ? 'Hide charts' : 'Show charts'}
          </button>
          {showCharts && (
            <>
              <HBarChart
                title="Engine quota vs current quota, by church"
                note={`Computable churches only (${computable.length} of ${run.results.length}), highest first.`}
                series={[
                  { label: 'Engine quota', color: '#2f6f4f' },
                  { label: 'Current quota', color: '#b59a6f' },
                ]}
                rows={compareRows}
                format={(n) => fmt(n)}
              />
              <PieChart
                title="Share of total engine quota, by church"
                note="Each church's payable quota as a slice of the total (top 10; the rest grouped as “Others”)."
                slices={shareSlices}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Row({
  r,
  open,
  delta,
  onToggle,
}: {
  r: QuotaScheduleRow;
  open: boolean;
  delta: number | null;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className="quota-srow"
        style={{
          borderBottom: open ? 'none' : '1px solid var(--border)',
          textAlign: 'right',
          cursor: 'pointer',
          opacity: r.computable ? 1 : 0.7,
        }}
        onClick={onToggle}
      >
        <td style={{ textAlign: 'left', padding: 6, fontWeight: 600 }}>{r.name}</td>
        <td style={{ padding: 6 }}>{r.computable ? fmt(r.smoothed_assessable) : '—'}</td>
        <td style={{ padding: 6, fontWeight: 600 }}>{r.computable ? fmt(r.payable_quota) : '—'}</td>
        <td style={{ padding: 6 }}>{fmt(r.current_quota)}</td>
        <td style={{ padding: 6, ...(delta != null ? deltaStyle(delta) : {}) }}>
          {delta == null ? '—' : `${delta >= 0 ? '+' : '−'}${fmt(Math.abs(delta))}`}
        </td>
        <td style={{ textAlign: 'left', padding: 6, color: 'var(--text2)' }}>
          {!r.computable && (
            <span style={{ color: 'var(--red)' }}>
              <AlertTriangle size={11} style={{ verticalAlign: -1 }} /> manual assessment
            </span>
          )}
          {r.flags.map((f) => (
            <span
              key={f}
              style={{
                fontSize: 10,
                background: 'var(--border)',
                borderRadius: 4,
                padding: '1px 5px',
                marginLeft: 4,
              }}
            >
              {FLAG_LABEL[f] ?? f}
            </span>
          ))}
        </td>
      </tr>
      {open && (
        <tr style={{ borderBottom: '1px solid var(--border)' }}>
          <td colSpan={6} style={{ padding: 0 }}>
            <WhyBreakdown row={r} />
          </td>
        </tr>
      )}
    </>
  );
}

function Stat({
  label,
  value,
  style,
}: {
  label: string;
  value: string;
  style?: React.CSSProperties;
}) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, ...style }}>{value}</div>
    </div>
  );
}
